import React, { useEffect, useState, useCallback } from 'react';
import styles from './MagicUpload.module.scss';
import { useLocation, useSearchParams } from 'react-router-dom';
import DrawerContent from './components/DrawerContent';
import useMagicUploadLogic from './useMagicUploadLogic';
import NeedInfo from '../NeedInfo/NeedInfo';
import { MagicUploadLoading, MagicUploadSelected, needInfoSelected } from '../../assets/hostedassets';
import closeIcon from '../_custom/SideDrawer/close.svg';
import { MAGIC_UPLOAD, NEED_INFO } from './Utils/MagicUploadConstants';
import MagicUploadTabSelector from './components/MagicUploadTabSelector/MagicUploadTabSelector';
import { MagicUploadFileData } from './useFetchDataForMU';
import AIText from '../AIText/AIText';
import { useAppSelector } from '../../app/hooks';
import { USER_TYPE } from '../../utils/constants/user';
import NeedInfoV2 from '../NeedInfoV2/NeedInfoV2';
import { SOURCE } from '../NeedInfoV2/NeedInfoV2Constants';
import { useVisibilityConfig } from '../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../utils/constants/visibilityKeys';
import RequirementTabSelector from '../NeedInfoV2/components/RequirementTabSelector/RequirementTabSelector';
import { TAB_STATE, TabState } from '../NeedInfoV2/NeedInfoV2Constants';

type MagicUploadProps = {};
const MagicUpload: React.FC<MagicUploadProps> = (props: MagicUploadProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const disableAnimation = searchParams.get('disableAnimation') === 'true';
    const location = useLocation();
    const companyName = searchParams.get('companyName');
    const lenderName = searchParams.get('lenderName');
    const tabStateParam = searchParams.get('tabState') as 'new' | 'tagged' | 'all' | null;
    const [magicUploadTabState, setMagicUploadTabState] = useState<'new' | 'tagged' | 'all'>('all');
    const [requirementTabState, setRequirementTabState] = useState<TabState>(TAB_STATE.ACTIVE);
    const [loading, setLoading] = useState(false);
    const [fileData, setFileData] = useState<MagicUploadFileData[]>([]);
    const [requirementCounts, setRequirementCounts] = useState<{ activeCount: number; archivedCount: number }>({
        activeCount: 0,
        archivedCount: 0,
    });
    const { isVisible, toggleDrawer, drawerContent, changeDrawerContent } = useMagicUploadLogic();
    const { isVisible: isVisibleMu } = useVisibilityConfig();
    const user = useAppSelector((state) => state.user);
    const isPartner = user.userData.accountType === USER_TYPE.PARTNER;
    const isCompany = user.userData.accountType === USER_TYPE.COMPANY || location.pathname.includes('company');
    const isCompanySide = user.userData.accountType === USER_TYPE.COMPANY;
    const permissions = useAppSelector((state) => state.investor.permissions);
    const showMu = isVisibleMu(VISIBILITY_KEYS.MAGIC_UPLOAD.ENABLED);
    const [heading, setHeading] = useState<string>('');

    const handleCountsUpdate = useCallback((activeCount: number, archivedCount: number) => {
        setRequirementCounts({ activeCount, archivedCount });
    }, []);

    useEffect(() => {
        if (companyName || lenderName) {
            setHeading(`${companyName} <> ${lenderName}`);
        }
    }, [companyName, lenderName]);

    useEffect(() => {
        if (tabStateParam) {
            setMagicUploadTabState(tabStateParam);
            setSearchParams((currentParams) => {
                currentParams.delete('tabState');
                return currentParams;
            });
        }
    }, [tabStateParam, setSearchParams]);

    return (
        <>
            <div
                data-testid="backdrop"
                className={`${styles.backdrop} ${isVisible ? styles.show : styles.hide} ${disableAnimation ? styles.noAnimation : ''}`}
                onClick={toggleDrawer}
            ></div>
            <div className={`${styles.drawer} ${isVisible ? styles.show : styles.hide} ${disableAnimation ? styles.noAnimation : ''}`}>
                <div className={styles.DrawerHeader}>
                    <div className={styles.DrawerContentSelector}>
                        <div className={styles.DrawerTabsContainer}>
                            {searchParams.get('showNeedInfo') === 'true' && (
                                <div
                                    onClick={() => changeDrawerContent(NEED_INFO)}
                                    className={drawerContent === NEED_INFO ? styles.ActiveDrawerTab : styles.DrawerTab}
                                >
                                    <div className={drawerContent === NEED_INFO ? styles.ActiveTab : styles.NotActiveTab}>
                                        <img src={needInfoSelected} alt="NeedInfo" />
                                        <span className={drawerContent === NEED_INFO ? styles.ActiveText : styles.Text}>
                                            {searchParams.get(SOURCE.LENDER_REQ) === 'true' ? 'Lender Query' : 'Data Pending'}
                                        </span>
                                    </div>
                                </div>
                            )}
                            {showMu && searchParams.get('hideMu') !== 'true' && (
                                <div
                                    style={loading ? { pointerEvents: 'none' } : {}}
                                    onClick={() => changeDrawerContent(MAGIC_UPLOAD)}
                                    className={drawerContent === MAGIC_UPLOAD ? styles.ActiveDrawerTab : styles.DrawerTab}
                                >
                                    <div className={drawerContent === MAGIC_UPLOAD || loading ? styles.ActiveTab : styles.NotActiveTab}>
                                        <img
                                            src={
                                                drawerContent === MAGIC_UPLOAD
                                                    ? MagicUploadSelected
                                                    : loading
                                                    ? MagicUploadLoading
                                                    : MagicUploadSelected
                                            }
                                            alt="NeedInfo"
                                        />
                                        <span className={drawerContent === MAGIC_UPLOAD ? styles.ActiveText : styles.Text}>
                                            {loading && drawerContent !== MAGIC_UPLOAD ? <AIText text={'Uploading'} /> : 'Magic Upload'}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div
                            className={`${styles.TabSlideRightContianer} ${
                                drawerContent === MAGIC_UPLOAD || drawerContent === NEED_INFO ? styles.showTabs : styles.hideTabs
                            } ${disableAnimation ? styles.noAnimation : ''}`}
                        >
                            {drawerContent === MAGIC_UPLOAD ? (
                                <MagicUploadTabSelector tabState={magicUploadTabState} setTabState={setMagicUploadTabState} />
                            ) : drawerContent === NEED_INFO && !isPartner && !isCompanySide ? (
                                <RequirementTabSelector
                                    tabs={[
                                        {
                                            id: TAB_STATE.ACTIVE,
                                            label: 'Active',
                                            count: requirementCounts.activeCount,
                                        },
                                        {
                                            id: TAB_STATE.ARCHIVED,
                                            label: 'Archived',
                                            count: requirementCounts.archivedCount,
                                        },
                                    ]}
                                    activeTabId={requirementTabState}
                                    onTabChange={(tabId) => setRequirementTabState(tabId as TabState)}
                                />
                            ) : null}
                        </div>
                    </div>
                    <div className={styles.heading}>
                        <span>{heading}</span>
                    </div>
                    <div className={styles.closeIcon}>
                        <img onClick={() => toggleDrawer()} src={closeIcon} alt="close" />
                    </div>
                </div>
                {drawerContent === NEED_INFO ? (
                    <div style={{ height: '100%' }}>
                        <NeedInfoV2
                            setLoading={setLoading}
                            toggleDrawer={toggleDrawer}
                            tabState={requirementTabState}
                            fileData={fileData}
                            setFileData={setFileData}
                            hasEditPermission={!isCompany}
                            onCountsUpdate={handleCountsUpdate}
                        />
                    </div>
                ) : (
                    // <NeedInfo
                    //     docFetching={docFetching}
                    //     setDocFetching={setDocFetching}
                    //     setLoading={setLoading}
                    //     toggleDrawer={toggleDrawer}
                    //     tabState={tabState}
                    //     fileData={fileData}
                    //     setFileData={setFileData}
                    //     hasEditPermission={!isCompany}
                    // />
                    <DrawerContent
                        setFileData={setFileData}
                        fileData={fileData}
                        tabState={magicUploadTabState}
                        toggleDrawer={toggleDrawer}
                    />
                )}
            </div>
        </>
    );
};

export default MagicUpload;
